import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  formatPoNumber,
  formatQty,
  monthLabel,
  type ItemAvailabilityMonth,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { Button } from '../ui/Button';
import { money } from '../money';

export function ItemAvailabilityPanel({
  itemId,
  initialLocationId,
}: {
  itemId: string;
  initialLocationId?: string;
}) {
  const trpc = useTRPC();
  const [locationId, setLocationId] = useState(initialLocationId ?? '');
  const [year, setYear] = useState<number | undefined>();
  const locations = useQuery(trpc.inventory.locations.queryOptions());
  const q = useQuery(
    trpc.inventoryReservations.availability.queryOptions({
      itemId,
      locationId: locationId || undefined,
    }),
  );
  const monthly = useQuery(
    trpc.inventoryReservations.monthly.queryOptions({
      itemId,
      locationId: locationId || undefined,
      year,
    }),
  );
  const state = shownState(q, q.data);
  const monthlyState = shownState(monthly, monthly.data);
  const locationName = (id: string | null) =>
    id
      ? (locations.data?.find((location) => location.id === id)?.name ??
        'Location unavailable')
      : 'Source not proposed';
  return (
    <div className="space-y-4">
      <Field label="Stock location">
        <select
          className={field}
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
          disabled={!locations.data}
        >
          <option value="">All locations</option>
          {(locations.data ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </Field>
      <StaleNotice
        show={refreshFailed(locations, locations.data) || locations.isPaused}
        onRetry={() => void locations.refetch()}
      />
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconInventory}
          what="this item's availability"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <>
          <dl className="grid grid-cols-2 divide-x divide-y divide-border overflow-hidden rounded-[var(--radius)] border border-border">
            {[
              ['On hand', q.data.physicalHundredths],
              ['Free to reserve', q.data.freeHundredths],
              ['Quoted · no hold', q.data.quoteDemandHundredths],
              ['Reserved for work orders', q.data.reservedHundredths],
            ].map(([label, value]) => (
              <div key={label} className="p-3">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums">
                  {formatQty(value as number)}
                </dd>
              </div>
            ))}
          </dl>
          {q.data.deficitHundredths > 0 ? (
            <Notice tone="warn">
              Recorded commitments exceed physical stock by{' '}
              {formatQty(q.data.deficitHundredths)}. The office needs to reconcile these
              allocations.
            </Notice>
          ) : null}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Quote demand</h3>
            <p className="text-xs text-muted-foreground">
              Proposed quantities leave physical and free stock unchanged.
            </p>
            {q.data.quoteDemand.length ? (
              <ul className="divide-y divide-border text-sm">
                {q.data.quoteDemand.map((demand, i) => (
                  <li
                    key={`${demand.quoteId}:${demand.locationId}:${i}`}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <span className="flex gap-2">
                      <Link
                        to="/quotes/$quoteId"
                        params={{ quoteId: demand.quoteId }}
                        className="font-semibold hover:underline"
                      >
                        Open quote
                      </Link>
                      {demand.commercialJobId ? (
                        <Link
                          to="/jobs/$jobId"
                          params={{ jobId: demand.commercialJobId }}
                          className="font-semibold hover:underline"
                        >
                          Job
                        </Link>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {locationName(demand.locationId)}
                    </span>
                    <span className="tabular-nums">
                      {formatQty(demand.quantityHundredths)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No current quote demand at this location.
              </p>
            )}
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Work order commitments</h3>
            {q.data.commitments.length ? (
              <ul className="divide-y divide-border text-sm">
                {q.data.commitments.map((commitment, i) => (
                  <li
                    key={`${commitment.lineId}:${commitment.locationId}:${i}`}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <Link
                      to="/work-orders/$workOrderId"
                      params={{ workOrderId: commitment.workOrderId }}
                      className="font-semibold hover:underline"
                    >
                      Open work order
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {locationName(commitment.locationId)}
                    </span>
                    <span className="tabular-nums">
                      {formatQty(commitment.quantityHundredths)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No stock reserved at this location.
              </p>
            )}
          </section>
        </>
      ) : null}
      <section
        className="space-y-3 border-t border-border pt-4"
        aria-label="Monthly stock history and projection"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            Monthly stock and sales {monthly.data?.year}
          </h3>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={!monthly.data || monthly.isFetching}
              onClick={() => setYear(monthly.data!.year - 1)}
            >
              Previous year
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!monthly.data || monthly.isFetching}
              onClick={() => setYear(monthly.data!.year + 1)}
            >
              Next year
            </Button>
          </div>
        </div>
        <StaleNotice
          show={refreshFailed(monthly, monthly.data) || monthly.isPaused}
          onRetry={() => void monthly.refetch()}
        />
        {monthlyState !== 'ready' ? (
          <UnresolvedState
            state={monthlyState}
            icon={IconInventory}
            what="monthly stock history"
            error={monthly.error}
            onRetry={() => void monthly.refetch()}
          />
        ) : monthly.data ? (
          <>
            <p className="text-xs text-muted-foreground">
              As of{' '}
              {new Date(monthly.data.asOf).toLocaleString(undefined, {
                timeZone: monthly.data.timezone,
              })}{' '}
              · {monthly.data.timezone}. Unknown values are shown as —. Billed material
              value covers all locations, before separate discounts, adjustments and tax.
            </p>
            <StockTimeline months={monthly.data.months} />
            <div
              className="h-scroll rounded-[var(--radius)] border border-border"
              tabIndex={0}
              role="region"
              aria-label="Monthly quantities; scroll horizontally for all columns"
            >
              <table className="w-full min-w-[760px] text-xs">
                <caption className="sr-only">
                  Recorded and projected stock, allocation, receipts and billed material
                  value by month
                </caption>
                <thead className="bg-surface-2 text-left">
                  <tr>
                    {[
                      'Month',
                      'On hand',
                      'Reserved',
                      'Used',
                      'Scheduled need',
                      'Expected receipts',
                      'Draft receipts',
                      'Projected stock',
                      'Billed units · all locations',
                      'Billed material value · all locations',
                    ].map((label) => (
                      <th key={label} scope="col" className="px-3 py-2 font-semibold">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.data.months.map((month) => (
                    <tr
                      key={month.month}
                      className={`border-t border-border ${month.phase === 'projected' ? 'bg-surface-2' : ''}`}
                    >
                      <th
                        scope="row"
                        className="whitespace-nowrap px-3 py-2 text-left font-medium"
                      >
                        {monthLabel(month.month)}
                        <span className="block font-normal text-muted-foreground">
                          {month.phase}
                        </span>
                      </th>
                      {[
                        month.closingPhysicalHundredths,
                        month.closingReservedHundredths,
                        month.recordedUseHundredths,
                        month.scheduledDemandHundredths,
                        month.expectedIncomingHundredths,
                        month.draftIncomingHundredths,
                        month.projectedClosingPhysicalHundredths,
                        month.billedQuantityHundredths,
                      ].map((value, i) => (
                        <td key={i} className="px-3 py-2 text-right tabular-nums">
                          {value === null ? '—' : formatQty(value)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums">
                        {month.billedRevenueCents === null
                          ? '—'
                          : money(month.billedRevenueCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer py-3 font-semibold">
                Quantities without a usable date
              </summary>
              <dl className="grid grid-cols-2 gap-2">
                {[
                  [
                    'Incoming without date',
                    monthly.data.unplaced.undatedIncomingHundredths,
                  ],
                  ['Overdue incoming', monthly.data.unplaced.overdueIncomingHundredths],
                  [
                    'Undated or overdue drafts',
                    monthly.data.unplaced.draftUndatedIncomingHundredths,
                  ],
                  [
                    'Unscheduled material need',
                    monthly.data.unplaced.unscheduledDemandHundredths,
                  ],
                  [
                    'Unscheduled reserved stock',
                    monthly.data.unplaced.unscheduledReservedHundredths,
                  ],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatQty(value as number)}
                    </dd>
                  </div>
                ))}
              </dl>
            </details>
            <details className="text-xs">
              <summary className="cursor-pointer py-3 font-semibold">
                Incoming purchase orders ({monthly.data.incoming.length})
              </summary>
              <ul className="divide-y divide-border">
                {monthly.data.incoming.map((incoming) => (
                  <li key={incoming.purchaseOrderLineId} className="space-y-1 py-2">
                    <Link
                      to="/purchase-orders/$purchaseOrderId"
                      params={{ purchaseOrderId: incoming.purchaseOrderId }}
                      className="font-semibold hover:underline"
                    >
                      {formatPoNumber(incoming.poNumber)}
                    </Link>{' '}
                    · {incoming.status} · {formatQty(incoming.quantityHundredths)}
                    <p className="text-muted-foreground">
                      {locationName(incoming.locationId)} · Expected{' '}
                      {incoming.expectedDate ?? 'date unknown'}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
            <details className="text-xs">
              <summary className="cursor-pointer py-3 font-semibold">
                Work order demand ({monthly.data.requirements.length})
              </summary>
              <ul className="divide-y divide-border">
                {monthly.data.requirements.map((requirement) => (
                  <li key={requirement.lineId} className="space-y-1 py-2">
                    <Link
                      to="/work-orders/$workOrderId"
                      params={{ workOrderId: requirement.workOrderId }}
                      className="font-semibold hover:underline"
                    >
                      Work order #{requirement.workOrderNumber}
                    </Link>{' '}
                    · {formatQty(requirement.demandHundredths)} remaining ·{' '}
                    {formatQty(requirement.reservedHundredths)} reserved
                    <p className="text-muted-foreground">
                      {requirement.firstVisitAt
                        ? `First future task visit: ${new Date(requirement.firstVisitAt).toLocaleString(undefined, { timeZone: monthly.data.timezone })}`
                        : 'No future task visit linked'}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
            <details className="text-xs">
              <summary className="cursor-pointer py-3 font-semibold">
                History coverage and projection assumptions
              </summary>
              <p className="mb-2 text-muted-foreground">
                Physical history:{' '}
                {monthly.data.coverage.physicalHistoryFrom
                  ? new Date(
                      monthly.data.coverage.physicalHistoryFrom,
                    ).toLocaleDateString(undefined, { timeZone: monthly.data.timezone })
                  : 'unknown'}
                . Reconciled allocation history:{' '}
                {monthly.data.coverage.reservationHistoryFrom
                  ? new Date(
                      monthly.data.coverage.reservationHistoryFrom,
                    ).toLocaleDateString(undefined, { timeZone: monthly.data.timezone })
                  : 'unknown'}
                .
              </p>
              <ul className="list-disc space-y-2 pl-4 text-muted-foreground">
                {monthly.data.assumptions.map((assumption) => (
                  <li key={assumption}>{assumption}</li>
                ))}
              </ul>
            </details>
          </>
        ) : null}
      </section>
    </div>
  );
}

/** Presentation-only geometry: all historical and projected quantities come from the server. */
export function StockTimeline({ months }: { months: ItemAvailabilityMonth[] }) {
  const known = months
    .flatMap((month) => [
      month.closingPhysicalHundredths,
      month.projectedClosingPhysicalHundredths,
    ])
    .filter((value): value is number => value !== null);
  if (!known.length)
    return <Notice tone="neutral">Stock history is unknown for this period.</Notice>;
  const minimum = Math.min(0, ...known);
  const maximum = Math.max(1, ...known);
  const range = maximum - minimum;
  const x = (index: number) => 45 + (index * 360) / Math.max(1, months.length - 1);
  const y = (value: number) => 130 - ((value - minimum) * 110) / range;
  const series = [
    { key: 'closingPhysicalHundredths' as const, dash: undefined },
    { key: 'projectedClosingPhysicalHundredths' as const, dash: '5 4' },
  ];
  return (
    <figure className="rounded-[var(--radius)] border border-border p-3">
      <figcaption className="mb-2 text-xs text-muted-foreground">
        On-hand stock: solid = recorded, dashed = projected. Exact values and unknown
        months are in the table.
      </figcaption>
      <svg
        viewBox="0 0 430 165"
        className="w-full text-foreground"
        role="img"
        aria-label="Recorded and projected physical stock by month; exact values in the following table"
      >
        {[minimum, maximum].map((value) => (
          <g key={value}>
            <line
              x1={45}
              x2={405}
              y1={y(value)}
              y2={y(value)}
              stroke="var(--sw-border)"
            />
            <text
              x={40}
              y={y(value) + 3}
              textAnchor="end"
              fill="currentColor"
              fontSize={9}
            >
              {formatQty(value)}
            </text>
          </g>
        ))}
        {series.map((serie) => (
          <g key={serie.key}>
            {months.map((month, index) => {
              const value = month[serie.key];
              const previous = index ? months[index - 1]![serie.key] : null;
              return value === null ? null : (
                <g key={month.month}>
                  {previous !== null ? (
                    <line
                      x1={x(index - 1)}
                      y1={y(previous)}
                      x2={x(index)}
                      y2={y(value)}
                      stroke="currentColor"
                      strokeWidth={1.8}
                      strokeDasharray={serie.dash}
                    />
                  ) : null}
                  <circle
                    cx={x(index)}
                    cy={y(value)}
                    r={2.5}
                    fill={serie.dash ? 'var(--sw-surface)' : 'currentColor'}
                    stroke="currentColor"
                  />
                </g>
              );
            })}
          </g>
        ))}
        {months.map((month, index) => (
          <text
            key={month.month}
            x={x(index)}
            y={153}
            textAnchor="middle"
            fill="currentColor"
            fontSize={9}
          >
            {monthLabel(month.month).split(' ')[0]?.slice(0, 1)}
          </text>
        ))}
      </svg>
    </figure>
  );
}
